// eslint-disable-next-line no-restricted-imports
import Route from '@ember/routing/route';

import Controller from '@ember/controller';
import EmObject, { action } from '@ember/object';
import { inject as service } from '@ember/service';
import isDestroyed from 'cio/utils/is-destroyed';
import ApiErrorsService from 'cio/services/api-errors';
import CrumbsService, { Crumb } from 'cio/services/crumbs';
import ReleaseNotificationService from 'cio/services/release-notification';
import BackfillsService from 'cio/services/backfills';
import Transition from '@ember/routing/-private/transition';
import RouterService from '@ember/routing/router-service';
import Model from '@ember-data/model';
import DS from 'ember-data';
import AccountService from 'cio/services/account';

// REF for Dynamic Titles
// https://gist.github.com/machty/8413411
// Note: titleToken and title have been replaced with documentToken and documentTitle respectively

export default class BaseCIORoute extends Route {
  @service apiErrors!: ApiErrorsService;
  @service crumbs!: CrumbsService;
  @service releaseNotification!: ReleaseNotificationService;
  @service backfills!: BackfillsService;
  @service router!: RouterService; // not used here, but very often called by child classes
  @service store!: DS.Store;
  @service account!: AccountService;

  // placeholder implementation
  crumb(): Crumb | Crumb[] | null {
    return null;
  }

  scrollOnActivate = true;

  getCrumb() {
    let crumbs = this.crumb();

    if (crumbs) {
      if (!Array.isArray(crumbs)) {
        crumbs = [crumbs];
      }

      crumbs = crumbs.map((crumb) => EmObject.extend(crumb).create());
    }

    return crumbs;
  }

  activate() {
    let crumbs = this.getCrumb();

    if (crumbs) {
      crumbs.forEach((crumb) => this.crumbs.append(crumb));
    }

    if (this.scrollOnActivate) {
      window.scrollTo(0, 0);
    }

    this.releaseNotification.checkOnRouteChange.perform();
    if (this.account.isJourneysActivated) {
      this.backfills.fetch.perform();
    }
  }

  deactivate() {
    // prevent memory leaks during app teardown (esp. in tests):
    if (isDestroyed(this.router)) {
      return;
    }

    let crumbs = this.getCrumb();

    if (crumbs) {
      crumbs.forEach((crumb) => this.crumbs.rollback(crumb));
    }
  }

  resetController(_controller: Controller, isExiting: boolean, _transition: Transition) {
    if (isExiting) {
      return;
    }

    // When changing model within the same route, we need to manually
    // swap crumbs, as the deactivate/activate cycle doesn't run.
    let crumbs = this.getCrumb();

    if (!crumbs) {
      return;
    }

    let previousCrumb = this.crumbs.crumbs?.lastObject;

    if (previousCrumb) {
      this.crumbs.rollback(previousCrumb);
    }

    crumbs.forEach((crumb) => this.crumbs.append(crumb));
  }

  setTitle(title: string) {
    if (!title) {
      return;
    } else {
      window.document.title = title;
    }
  }

  rollbackModelFor() {
    let { routeName } = this;
    let model = this.modelFor(routeName) as Model | undefined;

    if (isDestroyed(this.store) || !model || isDestroyed(model) || model.get('isDeleted') || !model.get('isLoaded')) {
      return;
    }

    model.rollbackAttributes();
  }

  // This is to reload a dynamic crumb in place. i.e. when the import name
  // changes, the crumb should show that new name, not the old one.
  reloadCrumb() {
    let newCrumbs = this.getCrumb();

    if (!newCrumbs) {
      return;
    }

    newCrumbs.forEach((newCrumb) => this.crumbs.replace(newCrumb));
  }

  @action
  didTransition() {
    let docTitle = this.crumbs.createTitle();
    this.setTitle(docTitle);
    return true;
  }
}
